import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import Modal from './Modal';
import { useToast } from '../contexts/ToastContext';
import { adminAPI } from '../services/api';

interface InviteUser {
  email: string;
  role: string;
}

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onClose, onSuccess }) => {
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState<InviteUser[]>([{ email: '', role: 'User' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAddAnotherUser = () => {
    setUsers([...users, { email: '', role: 'User' }]);
  };

  const handleUserChange = (index: number, field: 'email' | 'role', value: string) => {
    const updatedUsers = [...users];
    updatedUsers[index][field] = value;
    setUsers(updatedUsers);
  };

  const handleRemoveUser = (index: number) => {
    if (users.length > 1) {
      setUsers(users.filter((_, i) => i !== index));
    }
  };

  const handleInvite = async () => {
    // Validate all users
    const validUsers = users.filter(u => u.email.trim() !== '');
    if (validUsers.length === 0) {
      setError('Please add at least one user with a valid email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const user of validUsers) {
      if (!emailRegex.test(user.email)) {
        setError(`Invalid email address: ${user.email}`);
        return;
      }
    }

    setError('');
    setLoading(true);

    try {
      // Send invitations for all users
      for (const user of validUsers) {
        await adminAPI.inviteUser({
          email: user.email,
          role: user.role,
        });
      }

      showSuccess(`Successfully sent ${validUsers.length} invitation(s)`);
      setUsers([{ email: '', role: 'User' }]);
      onSuccess();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send invitation';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setUsers([{ email: '', role: 'User' }]);
      setError('');
      onClose();
    }
  };

  const validUserCount = users.filter(u => u.email.trim() !== '').length;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon />
          <Typography variant="h6">Add Users</Typography>
        </Box>
      }
      maxWidth="md"
      actions={
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={loading || validUserCount === 0}
            sx={{ textTransform: 'none' }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              `INVITE ${validUserCount} USER${validUserCount !== 1 ? 'S' : ''}`
            )}
          </Button>
        </Box>
      }
    >
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Invite multiple users to join your organization by providing their email and role. Each user will receive an email invitation.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {users.map((user, index) => (
          <Box key={index} sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={user.email}
              onChange={(e) => handleUserChange(index, 'email', e.target.value)}
              disabled={loading}
              required
              error={user.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)}
              helperText={
                user.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)
                  ? 'Invalid email address'
                  : ''
              }
            />
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Role</InputLabel>
              <Select
                value={user.role}
                label="Role"
                onChange={(e) => handleUserChange(index, 'role', e.target.value)}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {users.length > 1 && (
              <Button
                onClick={() => handleRemoveUser(index)}
                disabled={loading}
                color="error"
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Remove
              </Button>
            )}
          </Box>
        ))}

        <Link
          component="button"
          type="button"
          onClick={handleAddAnotherUser}
          disabled={loading}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            cursor: loading ? 'not-allowed' : 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <AddIcon fontSize="small" />
          Add another user
        </Link>
      </Box>
    </Modal>
  );
};

export default InviteUserModal;
